import { getNumberFromQueryString } from '@if/core/utils'
import { Box, Button, Paper } from '@mui/material'
import { DataGrid, GridPaginationModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid'
import { useDocsAutomationByFilters } from '@src/api/document-api'
import { DocsAutomation, DocsAutomationFiltersType, EProcessingStatus } from '@src/types/automationDocument'
import { useDevTools } from '@src/types/constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDataGridColumns } from './components/DataGridColumns'
import { useSnackbar } from 'notistack'
import DeleteDocumentDialog from './components/DeleteDocumentDialog'
import MatchDialog from './components/MatchDialog'
import MessageDialog from './components/MessageDialog'
import DocumentListToolbar from './components/ToolBar'
import { useResponsiveColumns } from './components/useResponsiveColumns'

const DocumentList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const apiRef = useGridApiRef()
  const { enqueueSnackbar } = useSnackbar()
  const highlightedRef = useRef(false)

  const [filterKey, setFilterKey] = useState(0)
  const [selectedDeleteDocsAutomation, setSelectedDeleteDocsAutomation] = useState<DocsAutomation | null>(null)
  const [selectedUnknownDocsAutomation, setSelectedUnknownDocsAutomation] = useState<DocsAutomation | null>(null)
  const [selectedMessageDocsAutomation, setSelectedMessageDocsAutomation] = useState<DocsAutomation | null>(null)

  useEffect(() => {
    if (!searchParams.has('limit') || !searchParams.has('offset') || !searchParams.has('status')) {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev)
          if (!updated.has('limit')) updated.set('limit', '25')
          if (!updated.has('offset')) updated.set('offset', '0')
          if (!updated.has('status'))
            updated.set('status', [EProcessingStatus.Processing, EProcessingStatus.Processed, EProcessingStatus.Matched, EProcessingStatus.PartiallySent, EProcessingStatus.Failed].join(','))
          return updated
        },
        { replace: true },
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Save current document list URL with filters so we can return to it from ViewDocument
  useEffect(() => {
    sessionStorage.setItem('documentsListUrl', `/documents?${searchParams.toString()}`)
  }, [searchParams])

  const filterValues = useMemo<DocsAutomationFiltersType>(() => {
    const statusParam = searchParams.get('status')
    const tagsParam = searchParams.get('tags')
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 25),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      clientNameContains: searchParams.get('clientNameContains') || null,
      email: searchParams.get('email') || null,
      tagSearch: searchParams.get('tagSearch') || null,
      status: statusParam ? (statusParam.split(',') as EProcessingStatus[]) : null,
      tags: tagsParam ? tagsParam.split(',') : null,
      sortField: searchParams.get('sortField') || null,
      sortDirection: searchParams.get('sortDirection') || null,
    }
  }, [searchParams])

  const updatePartialFilterValues = useCallback(
    (newValues: Partial<DocsAutomationFiltersType>) => {
      setSearchParams((prev) => {
        const updatedSearchParams = new URLSearchParams(prev)
        Object.entries(newValues).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              updatedSearchParams.set(key, value.join(','))
            } else {
              updatedSearchParams.set(key, String(value))
            }
          } else {
            updatedSearchParams.delete(key)
          }
        })
        // Reset to first page when filters change (except limit/offset)
        if (!('limit' in newValues) && !('offset' in newValues)) {
          updatedSearchParams.set('offset', '0')
        }
        return updatedSearchParams
      })
    },
    [setSearchParams],
  )

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setSearchParams((prev) => {
        const updatedSearchParams = new URLSearchParams(prev)
        updatedSearchParams.set('limit', model.pageSize.toString())
        updatedSearchParams.set('offset', (model.page * model.pageSize).toString())
        return updatedSearchParams
      })
    },
    [setSearchParams],
  )

  const sortModel = useMemo<GridSortModel>(() => {
    const field = filterValues.sortField
    const direction = filterValues.sortDirection
    if (!field) return []
    return [{ field, sort: (direction as 'asc' | 'desc') || 'asc' }]
  }, [filterValues.sortField, filterValues.sortDirection])

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev)
        if (model.length > 0 && model[0].sort) {
          updated.set('sortField', model[0].field)
          updated.set('sortDirection', model[0].sort)
        } else {
          updated.delete('sortField')
          updated.delete('sortDirection')
        }
        updated.set('offset', '0')
        return updated
      })
    },
    [setSearchParams],
  )

  const onClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams({ limit: '25', offset: '0' }))
    setFilterKey((k) => k + 1)
  }, [setSearchParams])

  const onFiltersChange = useCallback(
    (newFilters: Partial<DocsAutomationFiltersType>) => updatePartialFilterValues(newFilters),
    [updatePartialFilterValues],
  )

  const handleOnCloseDelete = useCallback(() => {
    setSelectedDeleteDocsAutomation(null)
  }, [])

  const handleOnCloseUnknown = useCallback(() => {
    setSelectedUnknownDocsAutomation(null)
  }, [])

  const [data, isPending, , refetch] = useDocsAutomationByFilters(filterValues)

  const columnVisibilityModel = useResponsiveColumns()

  const columns = useDataGridColumns({
    onView: setSelectedUnknownDocsAutomation,
    onDelete: setSelectedDeleteDocsAutomation,
    onMessage: setSelectedMessageDocsAutomation,
  })

  // Update selected document when cache is refreshed
  useEffect(() => {
    if (selectedUnknownDocsAutomation) {
      const updatedDoc = data.find((doc) => doc.id === selectedUnknownDocsAutomation.id)
      if (updatedDoc && updatedDoc.versionTag !== selectedUnknownDocsAutomation.versionTag) {
        setSelectedUnknownDocsAutomation(updatedDoc)
      }
    }
  }, [data, selectedUnknownDocsAutomation])

  // Scroll to and highlight the last viewed document when returning from detail page
  useEffect(() => {
    if (isPending || highlightedRef.current) return
    const lastViewedId = sessionStorage.getItem('lastViewedDocId')
    if (!lastViewedId) return

    highlightedRef.current = true
    sessionStorage.removeItem('lastViewedDocId')

    const rowIndex = data.findIndex((doc) => doc.id === lastViewedId)

    if (rowIndex === -1) {
      // Document is on another page — show snackbar with link
      enqueueSnackbar(t('docsAutomation.documentNotOnPage'), {
        variant: 'info',
        autoHideDuration: 6000,
        action: () => (
          <Button color="inherit" size="small" onClick={() => navigate(`/documents/${lastViewedId}`)}>
            {t('common.viewDocument')}
          </Button>
        ),
      })
      return
    }

    // Wait for DataGrid to finish rendering before scrolling
    setTimeout(() => {
      apiRef.current?.scrollToIndexes({ rowIndex, colIndex: 0 })

      // Highlight with fade-out after scroll completes
      setTimeout(() => {
        const rowEl = apiRef.current?.getRowElement(lastViewedId)
        if (rowEl) {
          rowEl.classList.add('highlight-row')
          setTimeout(() => rowEl.classList.remove('highlight-row'), 2500)
        }
      }, 100)
    }, 100)
  }, [data, isPending, apiRef, enqueueSnackbar, navigate, t])

  const hasNextPage = data.length >= filterValues.limit
  const currentPage = Math.floor(filterValues.offset / filterValues.limit)

  return (
    <>
      <Paper>
        <DocumentListToolbar key={filterKey} defaultValues={filterValues} onFiltersChange={onFiltersChange} onClearFilters={onClearFilters} onRefresh={refetch} showAssignEmails={useDevTools} />
        <Box data-tour="document-grid">
        <DataGrid
          disableColumnFilter
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          apiRef={apiRef}
          onRowClick={(params: { row: DocsAutomation }) => {
            sessionStorage.setItem('lastViewedDocId', params.row.id)
            navigate(`/documents/${params.row.id}`)
          }}
          getRowClassName={({ indexRelativeToCurrentPage }) => (indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : '')}
          rows={data}
          columns={columns}
          paginationModel={{ pageSize: filterValues.limit, page: currentPage }}
          onPaginationModelChange={onPaginationModelChange}
          loading={isPending}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          paginationMode="server"
          rowCount={isPending || hasNextPage ? -1 : filterValues.offset + data.length}
          localeText={{ noRowsLabel: t('common.noResults') }}
          sx={{
            height: '80vh',
            p: 0,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row.even-row': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
              {
                outline: 'none',
              },
            '& .MuiDataGrid-row.highlight-row': {
              animation: 'highlightFade 2.5s ease-out',
            },
            '@keyframes highlightFade': {
              '0%': { backgroundColor: 'rgba(25, 118, 210, 0.2)' },
              '100%': { backgroundColor: 'transparent' },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
        </Box>
      </Paper>
      <DeleteDocumentDialog docsAutomation={selectedDeleteDocsAutomation} onClose={handleOnCloseDelete} />
      <MatchDialog docsAutomation={selectedUnknownDocsAutomation} onClose={handleOnCloseUnknown} />
      <MessageDialog docsAutomation={selectedMessageDocsAutomation} onClose={() => setSelectedMessageDocsAutomation(null)} />
    </>
  )
}

export default DocumentList
